import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Axios from 'axios';
import moment from 'moment';
import Button from '~/components/button';
import Container from '~/components/container';
import Loading from '~/components/loading';
import RadioButton from '~/components/radioButton';
import { useAuth } from '~/hooks/contexts/useAuth';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import DateField from './DateField';
import PixModal, { NegotiationPix } from './PixModal';
import Constants from '~/utils/constants';
import { DebtNegotiationClientState } from '../ClientSearch';
import { ClientDebtColumn, ClientHeader } from '../shared/ClientPanels';
import { buildClientFields, summarizeInvoices } from '../shared/clientSummary';
import { DebtItem, buildDebtGroups } from '../shared/debt';
import NegotiationOffer from '../shared/NegotiationOffer';

import * as St from './styles';

interface DebtNegotiationDetail {
  cartaoTeucard?: {
    limiteCreditoUtilizado?: number;
    limiteEmprestimoUtilizado?: number;
    limiteExtraUtilizado?: number;
    limiteTotalUtilizado?: number;
    numeroCartao?: string;
    status?: string;
  };
  compras?: DebtItem[];
  comprasLimite12x?: DebtItem[];
  cpf?: string;
  elegivel?: boolean;
  emprestimos?: DebtItem[];
  financiamentos?: DebtItem[];
  fotoCliente?: string;
  idConsulta?: string;
  idFatura?: string;
  nome?: string;
  statusFatura?: string;
  valorTotalTransacoes?: number;
  valorTotalTransacoesComDesconto?: number;
  valorUltimaFatura?: number;
  vencimentoFatura?: string;
}

interface PaymentOption {
  idOpcaoPagamento: string;
  parcelas: number;
  taxaIOF?: number;
  valorComDesconto?: number;
  valorDesconto?: number;
  valorFinanciado?: number;
  valorIOF?: number;
  valorJuros?: number;
  valorOriginal?: number;
  valorParcela?: number;
  valorTotal?: number;
  vencimento?: string;
}

interface NegotiationCalculation {
  modalidade?: 'A_VISTA' | 'PARCELADO';
  opcoes?: PaymentOption[];
  percentualDesconto?: number;
  vencimento?: string;
}

const normalizeDetail = (payload: unknown): DebtNegotiationDetail | null => {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const nested =
    record.data && typeof record.data === 'object'
      ? (record.data as Record<string, unknown>)
      : null;
  const source =
    record.cartaoTeucard || record.nome || record.cpf ? record : nested;

  if (!source) return null;

  return source as DebtNegotiationDetail;
};

const normalizeCalculation = (payload: unknown): NegotiationCalculation | null => {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const nested =
    record.data && typeof record.data === 'object'
      ? (record.data as Record<string, unknown>)
      : null;
  const source = Array.isArray(record.opcoes) ? record : nested;

  if (!source || !Array.isArray(source.opcoes)) return null;

  return source as NegotiationCalculation;
};

type NegotiationType = 'avista' | 'parcelado';

const DISCOUNT_RANGE: Record<NegotiationType, { min: number; max: number }> = {
  avista: { min: 10, max: 30 },
  parcelado: { min: 10, max: 20 },
};

const DebtNegotiationClient = (): JSX.Element => {
  const history = useHistory<DebtNegotiationClientState>();
  const { user } = useAuth();
  const searchClient = history.location.state;
  const [detail, setDetail] = useState<DebtNegotiationDetail | null>(null);
  const [feedback, setFeedback] = useState('');
  const [newDueDate, setNewDueDate] = useState<Date | null>(null);
  const [negotiationType, setNegotiationType] = useState<NegotiationType>('avista');
  const [installments, setInstallments] = useState<number | ''>('');
  const [calculation, setCalculation] = useState<NegotiationCalculation | null>(
    null,
  );
  const [discount, setDiscount] = useState(DISCOUNT_RANGE.avista.max);
  const [discountText, setDiscountText] = useState(String(DISCOUNT_RANGE.avista.max));
  const discountLimits = DISCOUNT_RANGE[negotiationType];
  const getEligibleClient = useApi<DebtNegotiationDetail>(
    Endpoints.debtNegotiation.getEligibleClient,
    ApiMethod.POST,
  );
  const calculateNegotiation = useApi<NegotiationCalculation>(
    Endpoints.debtNegotiation.calculateNegotiation,
    ApiMethod.POST,
  );
  const audioInputRef = useRef<HTMLInputElement>(null);
  const [creatingNegotiation, setCreatingNegotiation] = useState(false);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [pix, setPix] = useState<NegotiationPix | null>(null);
  const [pixModalOpen, setPixModalOpen] = useState(false);
  const [showRequired, setShowRequired] = useState(false);
  const negotiationLocked = !!pix;
  const calculationTimer = useRef<number>();
  const requestCalculation = (
    nextType: NegotiationType,
    nextDiscount: number,
    nextDate: Date | null,
  ) => {
    const idConsulta = detail?.idConsulta;

    window.clearTimeout(calculationTimer.current);

    if (!idConsulta || !nextDate) return;

    calculationTimer.current = window.setTimeout(async () => {
      try {
        const response = await calculateNegotiation.callApi({
          idConsulta,
          modalidade: nextType === 'avista' ? 'A_VISTA' : 'PARCELADO',
          percentualDesconto: nextDiscount,
          vencimento: moment(nextDate).format('YYYY-MM-DD'),
        });

        if (!(response.status >= 200 && response.status < 300)) {
          setCalculation(null);
          setFeedback(
            response.mensagem || 'Não foi possível calcular a negociação.',
          );
          return;
        }

        const nextCalculation = normalizeCalculation(response.data);

        if (!nextCalculation) {
          setCalculation(null);
          setFeedback('O cálculo não retornou as opções de pagamento.');
          return;
        }

        setFeedback('');
        setCalculation(nextCalculation);
      } catch (error) {
        setCalculation(null);
        setFeedback(
          error instanceof Error
            ? error.message
            : 'Não foi possível calcular a negociação.',
        );
      }
    }, 300);
  };
  const updateDiscount = (
    nextValue: number,
    range: { min: number; max: number } = discountLimits,
  ) => {
    const clamped = Math.min(range.max, Math.max(range.min, nextValue));
    setDiscount(clamped);
    setDiscountText(String(clamped));
    requestCalculation(negotiationType, clamped, newDueDate);
  };

  const selectNegotiationType = (type: NegotiationType) => {
    const range = DISCOUNT_RANGE[type];
    const nextDiscount = Math.min(range.max, Math.max(range.min, discount));
    setNegotiationType(type);
    setDiscount(nextDiscount);
    setDiscountText(String(nextDiscount));
    requestCalculation(type, nextDiscount, newDueDate);
  };

  const handleDiscountInput = (rawValue: string) => {
    const digits = rawValue.replace(/\D/g, '').slice(0, 2);
    setDiscountText(digits);

    if (!digits) return;

    const nextValue = Number(digits);

    if (nextValue >= discountLimits.min && nextValue <= discountLimits.max) {
      setDiscount(nextValue);
      requestCalculation(negotiationType, nextValue, newDueDate);
    }
  };

  const handleCreateNegotiation = async () => {
    const idConsulta = detail?.idConsulta;
    const idOpcaoPagamento = selectedOption?.idOpcaoPagamento;
    const cpfOperador = String(user?.cpf || '').replace(/\D/g, '');
    const cpfCliente = String(searchClient?.cpf || detail?.cpf || '').replace(
      /\D/g,
      '',
    );

    if (!newDueDate || !audioFile) {
      setShowRequired(true);
      setFeedback('');
      return;
    }

    if (!idConsulta || !idOpcaoPagamento || !cpfOperador || !cpfCliente) {
      setFeedback(
        'Calcule a negociação antes de confirmar. A opção de pagamento ainda não foi escolhida.',
      );
      return;
    }

    const formData = new FormData();
    formData.append('idConsulta', idConsulta);
    formData.append('idOpcaoPagamento', idOpcaoPagamento);
    formData.append('cpfOperador', cpfOperador);
    formData.append('cpfCliente', cpfCliente);
    formData.append('arquivo', audioFile, audioFile.name);

    setCreatingNegotiation(true);

    try {
      const response = await Axios.post(
        Endpoints.debtNegotiation.createNegotiation,
        formData,
        {
          headers: {
            apikey: `token ${localStorage.getItem(Constants.TOKEN_KEY)}`,
            Authorization: `Bearer ${localStorage.getItem(Constants.TOKEN_BEARER)}`,
            'Id-Sessao': localStorage.getItem(Constants.SESSION_ID) || '',
            'Id-Sistema': `${process.env.REACT_APP_ID_SISTEMA}`,
            'Id-User': localStorage.getItem(Constants.USER_ID) || '',
          },
        },
      );

      if (!(response.status >= 200 && response.status < 300)) {
        setFeedback(
          response.data?.mensagem || 'Não foi possível criar a negociação.',
        );
        return;
      }

      const nextPix = response.data?.pix ?? response.data?.data?.pix;

      if (!nextPix?.pixCopiaECola && !nextPix?.qrCodeBase64) {
        setFeedback('A negociação foi criada, mas o Pix não foi retornado.');
        return;
      }

      setFeedback('');
      setPix(nextPix);
      setPixModalOpen(true);
    } catch (error) {
      const errorMessage = error?.response?.data?.mensagem;
      const responseMessage = errorMessage ? String(errorMessage) : '';

      setFeedback(responseMessage || 'Não foi possível criar a negociação.');
    } finally {
      setCreatingNegotiation(false);
    }
  };

  const commitDiscountInput = () => {
    const nextValue = Number(discountText);

    if (!discountText || Number.isNaN(nextValue)) {
      updateDiscount(discountLimits.min);
      return;
    }

    updateDiscount(nextValue);
  };

  const paymentOptions = Array.isArray(calculation?.opcoes)
    ? calculation.opcoes.filter((option) => option.parcelas > 0)
    : [];
  const installmentOptions = paymentOptions.map((option) => option.parcelas);
  const selectedOption =
    paymentOptions.find((option) => option.parcelas === installments) ??
    paymentOptions[0];
  const entryValue = selectedOption?.valorParcela;
  const installmentValue = selectedOption?.valorParcela;
  const appliedDiscount = selectedOption?.valorDesconto;
  const debtTotal = selectedOption?.valorOriginal;
  const newInvoiceValue = selectedOption?.valorTotal;

  useEffect(() => {
    if (installmentOptions.length === 0) {
      setInstallments('');
      return;
    }

    setInstallments((current) =>
      typeof current === 'number' && installmentOptions.includes(current)
        ? current
        : installmentOptions[0],
    );
  }, [calculation]);

  useEffect(() => {
    const loadDetail = async () => {
      const preloadedDetail = normalizeDetail(searchClient?.eligibleDetail);

      if (preloadedDetail) {
        setDetail(preloadedDetail);
        return;
      }

      const cpfOperador = String(user?.cpf || '').replace(/\D/g, '');

      if (!searchClient?.cpf || !cpfOperador) {
        if (!searchClient?.cpf) {
          setFeedback('Cliente não informado para a consulta.');
        }
        return;
      }

      try {
        const response = await getEligibleClient.callApi({
          cpf: searchClient.cpf.replace(/\D/g, ''),
          cpfOperador,
        });

        if (!(response.status >= 200 && response.status < 300)) {
          setFeedback(
            response.mensagem || 'Não foi possível consultar o cliente.',
          );
          return;
        }

        const nextDetail = normalizeDetail(response.data);

        if (!nextDetail) {
          setFeedback('A consulta não retornou os dados do cliente.');
          return;
        }

        setDetail(nextDetail);
      } catch (error) {
        setFeedback(
          error instanceof Error
            ? error.message
            : 'Não foi possível consultar o cliente.',
        );
      }
    };

    loadDetail();
  }, [searchClient?.cpf, user?.cpf]);

  useEffect(
    () => () => {
      window.clearTimeout(calculationTimer.current);
    },
    [],
  );

  const card = detail?.cartaoTeucard;
  const debtGroups = buildDebtGroups(detail);
  const invoice = summarizeInvoices(
    detail?.valorUltimaFatura,
    detail?.valorTotalTransacoes,
  );
  const fields = buildClientFields(detail?.nome, card, searchClient);

  return (
    <Container>
      <Loading
        status={getEligibleClient.loading || calculateNegotiation.loading}
      />
      {feedback && <St.Feedback>{feedback}</St.Feedback>}
      <PixModal
        open={pixModalOpen && !!pix}
        pix={pix}
        onClose={() => setPixModalOpen(false)}
      />
      <ClientHeader
        photo={detail?.fotoCliente}
        name={detail?.nome}
        fields={fields}
      />

      <St.Boxes>
        <ClientDebtColumn
          dueDate={detail?.vencimentoFatura}
          invoiceStatus={detail?.statusFatura}
          currentInvoice={invoice.currentInvoice}
          futureInvoices={invoice.futureInvoices}
          debtTotal={invoice.debtTotal}
          groups={debtGroups}
        />
        <St.SideColumn>
          <St.Panel>
            <St.ConfigStack>
              <div>
                <St.PanelTitle>Configurar negociação</St.PanelTitle>
                <St.ConfigBlock>
                  <St.EntryColumn>
                    <St.PlainText>Vencimento *</St.PlainText>
                    <DateField
                      value={newDueDate}
                      minDate={moment().startOf('day').toDate()}
                      maxDate={moment().startOf('day').add(15, 'days').toDate()}
                      disabled={negotiationLocked}
                      onChange={(date) => {
                        setNewDueDate(date);
                        requestCalculation(negotiationType, discount, date);
                      }}
                    />
                    {showRequired && !newDueDate && (
                      <St.RequiredError>
                        Informe a data de vencimento.
                      </St.RequiredError>
                    )}
                  </St.EntryColumn>
                  <St.Subtitle>Tipo de configuração</St.Subtitle>
                  <St.TypeList>
                    <St.RadioRow>
                      <St.RadioOption $disabled={negotiationLocked}>
                        <RadioButton
                          name="negotiationType"
                          checked={negotiationType === 'avista'}
                          disabled={negotiationLocked}
                          onChange={() => selectNegotiationType('avista')}
                        />
                        <span>À vista</span>
                      </St.RadioOption>
                      <St.DiscountChip>
                        Desconto 
                        {' '}
                        {DISCOUNT_RANGE.avista.max}
                        %
                      </St.DiscountChip>
                    </St.RadioRow>
                    <St.RadioRow>
                      <St.RadioOption $disabled={negotiationLocked}>
                        <RadioButton
                          name="negotiationType"
                          checked={negotiationType === 'parcelado'}
                          disabled={negotiationLocked}
                          onChange={() => selectNegotiationType('parcelado')}
                        />
                        <span>Parcelado</span>
                      </St.RadioOption>
                      <St.DiscountChip>
                        Desconto 
                        {' '}
                        {DISCOUNT_RANGE.parcelado.max}
                        %
                      </St.DiscountChip>
                    </St.RadioRow>
                  </St.TypeList>
                  <St.RangeRow>
                    <St.RangeBlock>
                      <St.Range
                        type="range"
                        min={discountLimits.min}
                        max={discountLimits.max}
                        value={discount}
                        disabled={negotiationLocked}
                        onChange={(event) =>
                          updateDiscount(Number(event.target.value))
                        }
                      />
                      <St.RangeLimits>
                        <St.RangeLimit>
                          {discountLimits.min}
                          %
                        </St.RangeLimit>
                        <St.RangeLimit>
                          {discountLimits.max}
                          %
                        </St.RangeLimit>
                      </St.RangeLimits>
                    </St.RangeBlock>
                    <St.PercentField
                      value={discountText}
                      inputMode="numeric"
                      disabled={negotiationLocked}
                      onChange={(event) =>
                        handleDiscountInput(event.target.value)
                      }
                      onBlur={commitDiscountInput}
                    />
                    <St.PercentSuffix>%</St.PercentSuffix>
                  </St.RangeRow>
                  <NegotiationOffer
                    type={negotiationType}
                    locked={negotiationLocked}
                    onTypeChange={selectNegotiationType}
                    entryValue={entryValue}
                    discountValue={discountText}
                    onDiscountChange={handleDiscountInput}
                    onDiscountBlur={commitDiscountInput}
                    installments={installments}
                    installmentOptions={installmentOptions}
                    onInstallmentsChange={setInstallments}
                    installmentValue={installmentValue}
                    invoiceValue={newInvoiceValue}
                    appliedDiscount={appliedDiscount}
                    debtTotal={debtTotal}
                    actions={(
                      <St.SummaryActions>
                        {pix ? (
                          <Button
                            fullWidth
                            outlined
                            type="button"
                            onClick={() => setPixModalOpen(true)}
                          >
                            <span>Gerar Pix copia e cola</span>
                          </Button>
                        ) : (
                          <>
                            <input
                              ref={audioInputRef}
                              type="file"
                              accept="audio/wav, audio/wave, audio/x-wav"
                              style={{ display: 'none' }}
                              onChange={(event) => {
                                const nextFile = event.target.files?.[0] ?? null;
                                setAudioFile(nextFile);
                              }}
                            />
                            <St.EntryLabel>Áudio da negociação *</St.EntryLabel>
                            <Button
                              fullWidth
                              outlined
                              type="button"
                              onClick={() => audioInputRef.current?.click()}
                            >
                              <span>Subir arquivo</span>
                            </Button>
                            {audioFile && (
                              <St.EntryLabel>{audioFile.name}</St.EntryLabel>
                            )}
                            {showRequired && !audioFile && (
                              <St.RequiredError>
                                Selecione o arquivo de áudio da negociação.
                              </St.RequiredError>
                            )}
                            <Button
                              fullWidth
                              type="button"
                              loading={creatingNegotiation}
                              onClick={handleCreateNegotiation}
                            >
                              <span>Confirmar negociação</span>
                            </Button>
                          </>
                        )}
                        <Button
                          fullWidth
                          type="button"
                          onClick={() =>
                            history.push('/negociacao-dividas/buscar-cliente')
                          }
                        >
                          <span>Voltar</span>
                        </Button>
                      </St.SummaryActions>
                    )}
                  />
                </St.ConfigBlock>
              </div>
            </St.ConfigStack>
          </St.Panel>
        </St.SideColumn>
      </St.Boxes>
    </Container>
  );
};

export default DebtNegotiationClient;
